import { NextPage } from 'next';
import yup from '@/utils/yup.locale';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosResponse } from 'axios';

import AtErrorLabel from '@/components/atoms/AtErrorLabel';
import MlLabeledMailBox from '@/components/molecules/MlLabeledMailBox';
import OrContentHeadingArea from '@/components/organisms/OrContentHeadingArea';
import OrButtonArea from '@/components/organisms/OrButtonArea';
import TemplateNoLogin from '@/components/templates/TemplateNoLogin';

import { CustomPageProps } from '@/types/client/CutostomPageProps';
import api from '@/utils/api';
import { ApiUrl } from '@/utils/constants';
import { SendOnetimeUrlResponse } from '@/types/api/v1/send-onetime-url';

/**
 * メイン処理
 */
const AccountPasswordResend: NextPage<CustomPageProps> = () => {
  // エラーメッセージ
  const [pageErrorMsg, setPageErrorMsg] = useState('');
  // 実行結果メッセージ
  const [pageMsg, setPageMsg] = useState('');

  // 画面項目定義
  type FormModel = {
    mailAddress: string;
  };

  // 入力チェック設定
  const schema: yup.ObjectSchema<FormModel> = yup.object().shape({
    mailAddress: yup
      .string()
      .label('メールアドレス')
      .required()
      .max(256)
      .email()
      .isValidEmailAddress('メールアドレス'),
  });

  // React Hook Form の設定
  type Form = yup.InferType<typeof schema>;
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<Form>({
    resolver: yupResolver(schema) as any,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  /**
   * メールを送信ボタン押下
   */
  const clickMailSendButton = async () => {
    // メッセージの初期化
    setPageErrorMsg('');
    setPageMsg('');

    // パラメータの設定
    const params = {
      mailAddress: getValues('mailAddress'),
    };

    await api
      .post(ApiUrl.NonProtected.ONETIME_PASSWORD, params)
      .then((res: AxiosResponse<SendOnetimeUrlResponse>) => {
        if (res.data.result_code === '00') {
          setPageMsg(
            'ご登録のメールアドレスにワンタイムパスワードを送信しました。メールをご確認ください。'
          );
        } else {
          setPageErrorMsg('入力されたメールアドレスは登録されていません。');
        }
      })
      .catch(() => {
        // エラー処理は共通処理で実施する
      })
      .finally();
  };

  /**
   * 画面レイアウト
   */
  return (
    <TemplateNoLogin>
      <OrContentHeadingArea>パスワードをお忘れの場合</OrContentHeadingArea>
      <div>
        ご登録のメールアドレスに送られるパスワード再設定用URLにて、再度パスワードを設定する必要があります。
        <br />
        ご登録のメールアドレスを入力し、[メールを送信]ボタンを押してください。
      </div>
      <div className='formArea'>
        <div className='taCenter'>
          <div>
            <MlLabeledMailBox
              id='mailAddress'
              name='mailAddress'
              labelClassName='w120'
              inputClassName='w250'
              register={register('mailAddress')}
            >
              メールアドレス
            </MlLabeledMailBox>
          </div>
          <AtErrorLabel testid='error_mailAddress'>{errors.mailAddress?.message}</AtErrorLabel>
        </div>
      </div>
      <OrButtonArea
        backLabel='戻る'
        backUrl='/account/login'
        mainLabel='メールを送信'
        onClick={handleSubmit(clickMailSendButton)}
      />
      <div className='taCenter mt15'>
        {pageErrorMsg && <AtErrorLabel testid='error_page_error'>{pageErrorMsg}</AtErrorLabel>}
        {pageMsg && <span data-testid='page_message'>{pageMsg}</span>}
      </div>
    </TemplateNoLogin>
  );
};
AccountPasswordResend.getInitialProps = () => {
  return { title: 'ワンタイムURL発行' };
};
export default AccountPasswordResend;
